import {FC, useState} from "react";
import {Copy, Check} from "lucide-react";
import {PREVIEW_URL} from "api/axios";

interface Props {
  token: string;
  shareUrl: string;
  onClose: () => void;
}

const ShareModal: FC<Props> = ({token, shareUrl, onClose}) => {
  const [copied, setCopied] = useState(false);
  const ogImageUrl = `${PREVIEW_URL}/og-image/${token}`;

  const copy = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const openLinkedIn = () =>
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, "_blank");

  const openTwitter = () => {
    const text = "Check out my verified accountant profile on BooksMatch";
    window.open(
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(text)}`,
      "_blank",
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-2xl overflow-hidden bg-brand-cream shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-brand-border">
          <h2 className="text-base font-bold text-brand-navy">Share Your Profile</h2>
          <button onClick={onClose} className="text-sm text-brand-muted hover:text-brand-navy transition-colors">
            ✕
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide mb-2 text-brand-muted">Share Card Preview</p>
            <div className="rounded-xl overflow-hidden border border-brand-border aspect-[240/126] relative">
              <img src={ogImageUrl} alt="Profile share card" className="w-full block relative z-[10]" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-cream/80 to-transparent z-[5] w-full h-full" />
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wide mb-2 text-brand-muted">Profile Link</p>
            <div className="flex gap-2">
              <div className="flex-1 px-3 py-2 rounded-xl text-xs truncate border border-brand-border bg-white text-brand-muted">
                {shareUrl}
              </div>
              <button
                onClick={copy}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold bg-brand-navy text-white transition-opacity hover:opacity-90 flex-shrink-0"
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? "Copied!" : "Copy"}
              </button>
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wide mb-2 text-brand-muted">Share On</p>
            <div className="flex gap-2">
              <button
                onClick={openLinkedIn}
                className="flex grow py-2.5 rounded-xl text-sm font-semibold text-white justify-center gap-2 items-center transition-opacity hover:opacity-90"
                style={{backgroundColor: "#0A66C2"}}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  className="lucide lucide-linkedin w-4 h-4"
                  data-loc="client/src/pages/AccountantProfile.tsx:517"
                >
                  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                  <rect width="4" height="12" x="2" y="9" />
                  <circle cx="4" cy="4" r="2" />
                </svg>
                LinkedIn
              </button>
              <button
                onClick={openTwitter}
                className="flex grow py-2.5 rounded-xl text-sm font-semibold text-white justify-center gap-2 items-center bg-black transition-opacity hover:opacity-90"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  className="lucide lucide-twitter w-4 h-4"
                  data-loc="client/src/pages/AccountantProfile.tsx:524"
                >
                  <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z" />
                </svg>
                X / Twitter
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
